<template>
    <div>
        <ts-page-title
            :title="$t('bankProfile.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                {
                    text: $t('bankProfile.pageTitle'),
                    active: true
                }
            ]"
        />
        <ts-panel>
            <ts-panel-wrapper>
                <div class="d-flex justify-content-between">
                    <ts-button outline color="info" @click.prevent="addNew">{{
                        $t("addNew")
                    }}</ts-button>
                    <div class="d-flex">
                        <Input
                            search
                            v-model="search"
                            :placeholder="$t('branch.search')"
                            :style="width"
                            @on-focus="() => (width = 'width: 500px')"
                            @on-blur="() => (width = 'width: 300px')"
                        />
                    </div>
                </div>
            </ts-panel-wrapper>
            <Table
                :columns="columns"
                :data="resources"
                size="small"
                :loading="loading"
                highlight-row
                stripe
                max-height="700"
            >
                <template slot-scope="{ row }" slot="bankLogo">
                    <img
                        class="tw-rounded tw-h-24 tw-w-24"
                        v-lazy="{
                            src: row.bank_logo,
                            error: require('@/assets/default-logo.jpg'),
                            loading: require('@/assets/Rolling-1s-200px.svg'),
                            preLoad: 1.3,
                            attempt: 1
                        }"
                    />
                </template>
                <template slot-scope="{ row }" slot="accountNumber">
                    <td
                        v-for="(a, index) in row.accounts"
                        class="tw-px-2"
                        style="border-bottom: none;"
                        :key="index"
                    >
                        <div
                            class="tw-border tw-float-left tw-p-2 tw-rounded"
                            style="width: fit-content"
                        >
                            <p class="tw-mb-0" v-if="a.prefix">
                                <span>Prefix: </span>
                                <span
                                    class="tw-text-green-500 tw-font-extrabold tw-tracking-wider"
                                    >{{ a.prefix }}</span
                                >
                            </p>
                            <p class="tw-mb-0">
                                <span>Account Number: </span>
                                <span
                                    class="tw-text-blue-600 tw-font-extrabold tw-tracking-wider"
                                    >{{ a.account_number }}</span
                                >
                            </p>
                            <p class="tw-mb-0">
                                <span>Account Name: </span>
                                <span class="tw-text-yellow-700">{{
                                    a.account_name
                                }}</span>
                            </p>
                        </div>
                    </td>
                </template>
                <template slot-scope="{ row }" slot="action">
                    <a
                        href="#"
                        class="text-primary ml-2"
                        @click.prevent="edit(row)"
                        v-tooltip="$t('edit')"
                    >
                        <Icon type="ios-create" size="20" />
                    </a>
                    <Poptip
                        confirm
                        :title="$t('areYouSure')"
                        @on-ok="deleteRecord(row)"
                        :transfer="true"
                        :ok-text="$t('yes')"
                        :cancel-text="$t('no')"
                    >
                        <a
                            class="ml-2 text-danger"
                            v-tooltip="$t('delete')"
                            :disabled="row._deleting"
                        >
                            <i
                                class="fas fa-circle-notch fa-spin"
                                v-if="row._deleting"
                            ></i>
                            <Icon type="ios-trash" size="20" v-else />
                        </a>
                    </Poptip>
                </template>
            </Table>
            <div class="tw-flex tw-items-center tw-justify-end tw-p-4">
                <ts-i-pagination
                    v-model="pagination"
                    @navigate="fetchData"
                ></ts-i-pagination>
            </div>
            <!-- form-action -->
            <Modal
                v-model="showForm"
                draggable
                sticky
                scrollable
                :mask="false"
                :footer-hide="true"
                :z-index="1022"
                :title="$t('bankProfile.pageTitle')"
                width="650px"
            >
                <from-action @cancel="clearEdit" ref="form_action" />
            </Modal>
        </ts-panel>
    </div>
</template>

<script>
import { debounce } from "lodash";
import { mapState } from "vuex";
import FromAction from "./form";

export default {
    name: "bankProfile",
    components: {
        FromAction
    },
    data() {
        return {
            loading: false,
            showForm: false,
            width: "width: 300px"
        };
    },
    computed: {
        ...mapState("corporate/bankProfile", ["resources", "pagination"]),
        search: {
            get() {
                return this.$store.state.corporate.bankProfile.search;
            },
            set(newValue) {
                this.$store.commit(
                    "corporate/bankProfile/SET_SEARCH",
                    newValue
                );
                this.$store.commit("corporate/bankProfile/RESET_CURRENT_PAGE");
            }
        },
        columns() {
            return [
                {
                    title: this.$t("bankProfile.logo"),
                    slot: "bankLogo"
                },
                {
                    title: this.$t("bankProfile.bankName"),
                    key: "bank_name"
                },
                {
                    title: this.$t("bankProfile.accountNumber"),
                    slot: "accountNumber",
                    width: 1120
                },
                {
                    title: this.$t("bankProfile.description"),
                    key: "description"
                },
                {
                    title: this.$t("actions"),
                    key: "action",
                    slot: "action",
                    fixed: "right",
                    align: "center",
                    width: 90
                }
            ];
        }
    },
    methods: {
        fetchData(attributes) {
            this.loading = true;
            this.$store
                .dispatch("corporate/bankProfile/fetch", { ...attributes })
                .then(() => {
                    this.loading = false;
                })
                .catch(error => {
                    this.loading = false;
                    this.notice({ type: "error", text: error.message });
                });
        },
        addNew() {
            this.showForm = true;
            this.$refs.form_action.clearInput()
            this.$store.commit("corporate/bankProfile/SET_EDIT_DATA", {});
        },
        edit(record) {
            this.$store.commit("corporate/bankProfile/SET_EDIT_DATA", record);
            this.showForm = true;
            this.$refs.form_action.setEditData()
        },
        clearEdit() {
            this.$store.commit("corporate/bankProfile/SET_EDIT_DATA", {});
            this.showForm = false;
            this.fetchData();
        },
        deleteRecord(record) {
            record._deleting = true;
            this.$store
                .dispatch("corporate/bankProfile/destroy", record.bank_id)
                .then(response => {
                    this.notice({
                        type: "success",
                        text: response.message
                    });
                    this.fetchData();
                })
                .catch(error => {
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => {
                    record._deleting = false;
                });
        },
        notice(not) {
            this.$Notice[not.type]({
                title: "BANK PROFILE",
                desc: not.text
            });
        }
    },
    watch: {
        search: debounce(function() {
            this.fetchData();
        }, 500)
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.fetchData();
        });
    },
    beforeRouteLeave(to, from, next) {
        this.$store.commit("corporate/bankProfile/RESET_STATE");
        next();
    }
};
</script>
